import { IcColorStatusS, IcColorStatusSActive } from '@assets/icons/24x24';
import { Button, For, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { SensorOrSubSensorInfoByType } from '@components/cockpit/panels/monitoring/monitoring.types';
import PopupContainer from '@components/map/popup/common/PopupContainer';
import { useCameraCommand } from '@hooks/useCameraCommand';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { SensorTypeEnum, VideoStateEnum } from '@/types/sensor/sensors';

type Props = {
  sensorOrSubSensorInfo: SensorOrSubSensorInfoByType<SensorTypeEnum.AF2206>;
  replayMode: boolean;
};

type SubCam = { name: string; fluxId: number };

const subCameras: SubCam[] = [
  { name: 'PC/GC', fluxId: 0 },
  { name: 'SWIR', fluxId: 2 },
  { name: 'MWIR', fluxId: 3 },
];

export default function HolopticsMaintenanceActions({ replayMode, sensorOrSubSensorInfo }: Readonly<Props>) {
  const isSubCameraActive = (cameraId: number) =>
    sensorOrSubSensorInfo.status?.videoStatuses.find((value) => value.id === cameraId)?.state === VideoStateEnum.ON;

  const useCamera = useCameraCommand({
    monitoringSensor: sensorOrSubSensorInfo,
  });

  const [videoPowerLoadings, setVideoPowerLoadings] = useState<{ cameraId: number; button: 'turnOff' | 'turnOn' }[]>(
    [],
  );

  const isVideoPowerLoading = (cameraId: number, button?: 'turnOff' | 'turnOn') => {
    return videoPowerLoadings.some((value) => value.cameraId === cameraId && (value.button === button || !button));
  };

  const toggleSubCameraActive = (cameraId: number, button: 'turnOff' | 'turnOn') => {
    if (isVideoPowerLoading(cameraId) || replayMode) {
      return;
    }
    useCamera.videoPower(button === 'turnOn', cameraId);
    setVideoPowerLoadings((prevState) => [...prevState, { cameraId, button }]);
    setTimeout(async () => {
      setVideoPowerLoadings((prevState) => prevState.filter((value) => value.cameraId !== cameraId));
    }, 40000);
  };

  return (
    <PopupContainer>
      <VStack gap="standardS" width="100%">
        <For each={subCameras}>
          {(subCam) => {
            const isLoadingTurnOn = isVideoPowerLoading(subCam.fluxId, 'turnOn');
            const isLoadingTurnOff = isVideoPowerLoading(subCam.fluxId, 'turnOff');
            const isLoading = isVideoPowerLoading(subCam.fluxId);
            return (
              <HStack gap="standardS" width="100%" key={subCam.fluxId}>
                <HStack width="88px">
                  <Text textStyle="screenStd">{subCam.name}</Text>
                  <Icon
                    marginLeft="auto"
                    color="skyBlue.500"
                    boxSize={{ base: '16px', '4k': '24px' }}
                    as={isSubCameraActive(subCam.fluxId) ? IcColorStatusSActive : IcColorStatusS}
                  />
                </HStack>
                <HStack gap={0} width="100%">
                  <Button
                    variant="toggle"
                    size="toggleM"
                    flex={1}
                    zIndex={isLoadingTurnOn ? 1 : 0}
                    _hover={{ zIndex: 2 }}
                    disabled={replayMode || isLoading}
                    data-active={isLoadingTurnOn}
                    loading={isLoadingTurnOn}
                    onClick={() => !isLoadingTurnOn && toggleSubCameraActive(subCam.fluxId, 'turnOn')}
                  >
                    <FormattedMessage id="global.turnOn" />
                  </Button>
                  <Button
                    variant="toggle"
                    size="toggleM"
                    marginLeft="-2px"
                    flex={1}
                    zIndex={isLoadingTurnOff ? 1 : 0}
                    _hover={{ zIndex: 2 }}
                    disabled={replayMode || isLoading}
                    data-active={isLoadingTurnOff}
                    loading={isLoadingTurnOff}
                    onClick={() => !isLoadingTurnOff && toggleSubCameraActive(subCam.fluxId, 'turnOff')}
                  >
                    <FormattedMessage id="global.turnOff" />
                  </Button>
                </HStack>
              </HStack>
            );
          }}
        </For>
      </VStack>
    </PopupContainer>
  );
}
